import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import * as actions from "../../_redux/sections/SectionsAction";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { SectionsFilter } from "./sections-filter/SectionsFilter";
import { SectionsTable } from "./sections-table/SectionsTable";
import { useSectionsUIContext } from "./SectionsUIContext";

export function SectionsCard() {
  // Sections UI Context
  const sectionsUIContext = useSectionsUIContext();
  const sectionsUIProps = useMemo(() => {
    return {
      ids: sectionsUIContext.ids,
      setIds: sectionsUIContext.setIds,
      queryParams: sectionsUIContext.queryParams,
      openNewSectionPage: sectionsUIContext.openNewSectionPage,
    };
  }, [sectionsUIContext]);
    
  // Getting curret state of Section list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.sections }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // Sections Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);
    
  /** Belli Bir Şube Listesini Excele Aktarır */
  const exportSectionsToExcel = () => {
    dispatch(actions.getAllSections(sectionsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [15, 15, 20, 15];
      const columns = ["Sınıf Düzeyi", "Sınıf", "Şube", "Kapasite"];
      entities.forEach(entity => rows.push([entity.gradeLevelName, entity.grade, entity.name, entity.capacity]));
      const filename = "Şube Listesi";
      exportToExcel({ columns, rows, widths, filename });
    });    
  }

  /** Belli Bir Şube Listesini PDF e Aktarır */
  const exportSectionsToPDF = () => {
    dispatch(actions.getAllSections(sectionsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Sınıf Düzeyi",
          rows: entities.map(entity => entity.gradeLevelName)
        },
        {
          header: "Sınıf",
          rows: entities.map(entity => entity.grade)
        },
        {
          header: "Şube",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Kapasite",
          rows: entities.map(entity => entity.capacity)
        }
      ];
  
      const filename = "Şube Listesi";
      exportToPDF(views, filename);
    });    
  }

  /** Belli Bir Şube Listesini Yazdırma Penceresini Açar */
  const printSections = () => {
    dispatch(actions.getAllSections(sectionsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = "Şube Listesi";
      const properties = ["Sınıf Düzeyi", "Sınıf", "Şube", "Kapasite"];

      entities.forEach(entity => {
        data.push({
          "Sınıf Düzeyi": entity.gradeLevelName,
          "Sınıf": entity.grade,
          "Şube": entity.name,
          "Kapasite": entity.capacity
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Şube Listesini Panoya Kopyalar */
  const copySections = () => {
    dispatch(actions.getAllSections(sectionsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const header = "Şube Listesi";
      const columns = ["Sınıf Düzeyi", "Sınıf", "Şube", "Kapasite"];
      entities.forEach(entity => rows.push([entity.gradeLevelName, entity.grade, entity.name, entity.capacity]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Şube Listesini Siler */
  const deleteSections = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();        
      return;
    }  
    deletionAlert().then(result => {
      if(result.isConfirmed) dispatch(actions.deleteSections(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        // refresh list after deletion
        dispatch(actions.getSections(sectionsUIProps.queryParams));
        // clear selections list
        sectionsUIProps.setIds([]);
      }, 2500);
    }
  };
  
  const title = <>Şube Listesi{sectionsUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{sectionsUIProps.ids.length}</span>]</> :
                  ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportSectionsToExcel}
                exportToPDF={exportSectionsToPDF}
                print={printSections}
                copy={copySections}
              />
            }
            {
              isAdditionAuthorized && 
              <FormButton
                path="Navigation/Plus"
                title="Yeni Şube"
                method={sectionsUIProps.openNewSectionPage}
              />
            }              
            {
              isDeletionAuthorized && 
              <DeleteButton
                title="Seçilenleri Sil"
                method={() => deleteSections(sectionsUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <SectionsFilter />
        <SectionsTable
          deleteSections={deleteSections}
        />
      </CardBody>
    </Card>
  );
}